import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import { Room, RoomVisibility } from "@/types/rooms";
import { ContentItem, ContentType } from "@/types/content";
import { ContentGrid } from "@/components/content/ContentGrid";
import { DashboardHeroStudy } from "@/components/dashboard/DashboardHeroStudy";
import { RoomModeProvider, useRoomMode } from "@/context/RoomModeContext";
import { RoomHeader } from "@/components/rooms/RoomHeader";
import { RoomExamCreationHero } from "@/components/rooms/RoomExamCreationHero";
import { RoomExamCreationGrid } from "@/components/rooms/RoomExamCreationGrid";
import { RoomExamStep2Reference, ReferenceResource } from "@/components/rooms/RoomExamStep2Reference";
import { RoomExamStep3Preferences } from "@/components/rooms/RoomExamStep3Preferences";
import { RoomExamLoading } from "@/components/rooms/RoomExamLoading";
import { RoomAITutorPanel } from "@/components/rooms/RoomAITutorPanel";

import { RoomActivityFeed } from "@/components/rooms/RoomActivityFeed";
import { RoomShareDialog } from "@/components/rooms/RoomShareDialog";
import { ContentShareDialog } from "@/components/content/ContentShareDialog";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { toast } from "sonner";
import { checkUsageLimit, UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { useTranslation } from "@/context/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { PublicAuthOverlay } from "@/components/public/PublicAuthOverlay";
import { useRooms } from "@/hooks/useRooms";
import { useContentDelete } from "@/hooks/useContentDelete";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Inner component that uses the UsageLimitContext (rendered inside DashboardShell)
const RoomExamStepHandler: React.FC<{
  mode: string;
  setMode: (mode: string) => void;
  selectedIds: string[];
  room: Room | null;
  roomId: string | undefined;
  navigate: ReturnType<typeof useNavigate>;
  referenceResources: ReferenceResource[] | null;
}> = ({ mode, setMode, selectedIds, room, roomId, navigate, referenceResources }) => {
  const { showLimitError } = useUsageLimitContext();

  const handlePreferencesStart = useCallback(async (prefs?: {
    questionCount: number;
    questionTypes: string[];
    examDuration: number | null;
  }) => {
    if (!prefs) {
      prefs = {
        questionCount: 25,
        questionTypes: ["Multiple Choice"],
        examDuration: null,
      };
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to create an exam");
        return;
      }

      // Check usage limit before creating exam
      const check = await checkUsageLimit("exams");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("exams", check.used, check.limit));
        return;
      }

      setMode("exam-loading");

      // Build reference_payload from collected resources
      const refPayload = referenceResources && referenceResources.length > 0
        ? referenceResources.map((r) => ({
            type: r.type,
            name: r.name,
            value: typeof r.value === "string" ? r.value : r.name,
          }))
        : null;

      // Create exam record in database
      const { data: exam, error } = await supabase
        .from("exams")
        .insert({
          user_id: user.id,
          room_id: roomId,
          content_ids: selectedIds.length > 0 ? selectedIds : null,
          question_count: prefs.questionCount,
          question_types: prefs.questionTypes,
          duration_minutes: prefs.examDuration,
          title: `${room?.name || 'Untitled'} Exam`,
          status: 'pending',
          reference_payload: refPayload,
        })
        .select()
        .single();

      if (error) throw error;

      // Call edge function to generate exam questions
      const { error: genError } = await supabase.functions.invoke("generate-exam", {
        body: { exam_id: exam.id }
      });

      if (genError) {
        console.error("Error generating exam:", genError);
      }

      toast.success("Exam created! Generating questions...");
      
      navigate(`/room/${roomId}/exam/${exam.id}`);
    } catch (error) {
      console.error("Error creating exam:", error);
      toast.error("Failed to create exam");
      setMode("exam-step-3");
    }
  }, [showLimitError, setMode, roomId, selectedIds, room, navigate, referenceResources]);

  if (mode === "exam-step-3") {
    return (
      <RoomExamStep3Preferences
        onBack={() => setMode("exam-step-2")}
        onSkip={() => handlePreferencesStart()}
        onStart={handlePreferencesStart}
      />
    );
  }

  return null;
};

const RoomPageInner: React.FC = () => {
  usePageMeta("Room — Shattara AI", "", { noIndex: true });
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { mode, setMode } = useRoomMode();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();

  const [room, setRoom] = useState<Room | null>(null);
  const [contents, setContents] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTutorOpen, setIsTutorOpen] = useState(false);
  const [referenceResources, setReferenceResources] = useState<ReferenceResource[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isOwnerOrMember, setIsOwnerOrMember] = useState(false);
  const [isTeamAdmin, setIsTeamAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isPublicView, setIsPublicView] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ContentItem | null>(null);
  const [contentShareOpen, setContentShareOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState<ContentItem | null>(null);

  const { rooms: allPersonalRooms, teamRooms } = useRooms();
  const allRooms = useMemo(() => [...allPersonalRooms, ...teamRooms], [allPersonalRooms, teamRooms]);
  const { deleteContent, deleting } = useContentDelete();

  const handleContentShare = (item: ContentItem) => {
    setItemToShare(item);
    setContentShareOpen(true);
  };

  const handleContentDeleteClick = (item: ContentItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    const success = await deleteContent(itemToDelete.id);
    if (success) {
      setContents(prev => prev.filter(c => c.id !== itemToDelete.id));
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleAddToRoom = async (item: ContentItem, targetRoomId: string) => {
    const { error } = await supabase
      .from("content")
      .update({ room_id: targetRoomId })
      .eq("id", item.id);

    if (error) {
      toast.error("Failed to move content to room");
      return;
    }

    const targetRoom = allRooms.find(r => r.id === targetRoomId);
    toast.success(`Moved to ${targetRoom?.name || "room"}`);

    // If moved to a different room, remove from current view
    if (targetRoomId !== roomId) {
      setContents(prev => prev.filter(c => c.id !== item.id));
    }
  };

  const handleContentVisibilityChange = (contentId: string, visibility: "private" | "public") => {
    setContents(prev =>
      prev.map(item =>
        item.id === contentId ? { ...item, visibility } : item
      )
    );
  };

  // Fetch room data and contents
  useEffect(() => {
    if (authLoading) return;

    const fetchRoomData = async () => {
      if (!roomId) {
        toast.error("Room ID not found");
        navigate("/dashboard");
        return;
      }

      setLoading(true);

      try {
        setCurrentUserId(user?.id ?? null);

        // Fetch room details
        const { data: roomData, error: roomError } = await supabase
          .from("rooms")
          .select("*")
          .eq("id", roomId)
          .maybeSingle();

        if (roomError) {
          console.error("Error fetching room:", roomError);
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!roomData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const isOwner = user ? roomData.user_id === user.id : false;
        const isPublic = roomData.visibility === "public";

        // Check if user is a team member for team rooms
        let isTeamMember = false;
        let teamAdmin = false;
        if (!isOwner && user && roomData.team_id) {
          const { data: membership } = await supabase
            .from("team_members")
            .select("id, role")
            .eq("team_id", roomData.team_id)
            .eq("user_id", user.id)
            .maybeSingle();
          isTeamMember = !!membership;
          teamAdmin = !!membership && (membership.role === 'owner' || membership.role === 'admin');
        }

        if (!isOwner && !isTeamMember && !isPublic) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setRoom(roomData as Room);
        setIsOwnerOrMember(isOwner || isTeamMember);
        setIsTeamAdmin(isOwner || teamAdmin);
        setIsPublicView(!isOwner && !isTeamMember);

        // Fetch room contents
        const { data: contentData, error: contentError } = await supabase
          .from("content")
          .select("*")
          .eq("room_id", roomId)
          .order("created_at", { ascending: false });

        if (contentError) {
          console.error("Error fetching contents:", contentError);
        } else {
          const mappedContents: ContentItem[] = (contentData || []).map((item) => ({
            id: item.id,
            title: item.title,
            type: item.type as ContentType,
            createdAt: item.created_at,
            thumbnailUrl: item.thumbnail_url,
            sourceUrl: item.source_url,
            storagePath: item.storage_path,
            roomId: item.room_id,
            updatedAt: item.updated_at,
            visibility: item.visibility as "private" | "public",
          }));
          setContents(mappedContents);
        }
      } catch (error) {
        console.error("Error in fetchRoomData:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchRoomData();
  }, [roomId, navigate, user, authLoading]);

  // Setup realtime subscription for room updates
  useEffect(() => {
    if (!roomId) return;

    const channel = supabase
      .channel(`room-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${roomId}`
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            setRoom(payload.new as Room);
          } else if (payload.eventType === 'DELETE') {
            toast.error("This room has been deleted");
            navigate("/dashboard");
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'content',
          filter: `room_id=eq.${roomId}`
        },
        async () => {
          // Refetch contents on any change
          const { data: contentData } = await supabase
            .from("content")
            .select("*")
            .eq("room_id", roomId)
            .order("created_at", { ascending: false });

          if (contentData) {
            const mappedContents: ContentItem[] = contentData.map((item) => ({
              id: item.id,
              title: item.title,
              type: item.type as ContentType,
              createdAt: item.created_at,
              thumbnailUrl: item.thumbnail_url,
              sourceUrl: item.source_url,
              storagePath: item.storage_path,
              roomId: item.room_id,
              updatedAt: item.updated_at,
              visibility: item.visibility as "private" | "public",
            }));
            setContents(mappedContents);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, navigate]);

  useEffect(() => {
    if (mode === "exam" && contents.length > 0) {
      setSelectedIds(contents.map((c) => c.id));
    }
  }, [mode, contents]);

  const handleToggleItem = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === contents.length ? [] : contents.map((c) => c.id)
    );
  };

  const goToStep2 = () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one item");
      return;
    }
    setMode("exam-step-2");
  };


  const handleVisibilityChange = async (visibility: RoomVisibility) => {
    if (!room) return;
    
    try {
      const { error } = await supabase
        .from("rooms")
        .update({ visibility })
        .eq("id", room.id);
        
      if (error) throw error;
      
      setRoom({ ...room, visibility });
      toast.success(`Room is now ${visibility}`);
    } catch (error) {
      console.error("Error updating room visibility:", error);
      toast.error("Failed to update room visibility");
    }
  };

  const visibleContents = useMemo(() => contents, [contents]);

  const buildHref = (item: ContentItem) => `/content/${item.id}`;

  // Not found state
  if (!loading && !authLoading && notFound) {
    return (
      <DashboardShell isPublicView={!user}>
        <div className="flex h-[calc(100vh-56px)] flex-col items-center justify-center text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-2">Room not found</h2>
          <p className="text-muted-foreground">This room doesn't exist or is not publicly available.</p>
        </div>
      </DashboardShell>
    );
  }

  // Public read-only view
  if (isPublicView && !loading) {
    return (
      <DashboardShell isPublicView>
        <PublicAuthOverlay />
        <div className="mx-auto w-full px-3 pb-6 pt-6 sm:px-4 lg:px-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">{room?.name}</h1>
            {room?.description && (
              <p className="mt-2 text-muted-foreground">{room.description}</p>
            )}
          </div>
          {contents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <h3 className="mb-2 text-xl font-semibold text-foreground">No content yet</h3>
              <p className="text-muted-foreground">This room doesn't have any content.</p>
            </div>
          ) : (
            <ContentGrid
              items={contents}
              isLoading={false}
              buildHref={(item) => `/content/${item.id}`}
              viewMode="grid"
            />
          )}
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell room={room} onShareRoom={() => setIsShareDialogOpen(true)}>
      <div className="flex min-h-screen w-full">
        {/* Main content area that shifts left when panel opens */}
        <div className={`flex min-h-screen flex-1 flex-col transition-all duration-300 ease-in-out ${
          isTutorOpen ? 'mr-0 xl:mr-[576px]' : 'mr-0'
        }`}>
          <header className="sticky top-0 z-30 mt-4 bg-background/80 backdrop-blur-md">
            <div className="mx-auto w-full px-3 pt-4 sm:px-6 lg:px-8">
              <RoomHeader
                room={room}
                onOpenTutor={() => setIsTutorOpen(true)}
                isOwnerOrMember={isOwnerOrMember}
                isTutorOpen={isTutorOpen}
              />
            </div>
          </header>

          <main className="flex-1">
            <div className="mx-auto w-full px-3 pb-6 pt-6 sm:px-4 lg:px-6">
              {/* Activity feed for team rooms */}
              {room?.team_id && roomId && mode === "default" && (
                <RoomActivityFeed roomId={roomId} />
              )}
              {mode === "exam" && (
                <RoomExamCreationHero
                  totalCount={visibleContents.length}
                  selectedCount={selectedIds.length}
                  onToggleSelectAll={handleToggleSelectAll}
                  onContinue={goToStep2}
                />
              )}

              {mode === "exam-step-2" && (
                <RoomExamStep2Reference
                  onBack={() => setMode("exam")}
                  onSkip={() => { setReferenceResources(null); setMode("exam-step-3"); }}
                  onContinue={(res) => { setReferenceResources(res); setMode("exam-step-3"); }}
                />
              )}

              {/* RoomExamStepHandler is rendered inside DashboardShell so it has access to UsageLimitContext */}
              <RoomExamStepHandler
                mode={mode}
                setMode={setMode}
                selectedIds={selectedIds}
                room={room}
                roomId={roomId}
                navigate={navigate}
                referenceResources={referenceResources}
              />

              {mode === "exam-loading" && <RoomExamLoading />}

              {mode === "add-content" && (
                <section className="mb-8">
                  <DashboardHeroStudy roomId={roomId} />
                </section>
              )}

              {mode === "exam" ? (
                <RoomExamCreationGrid
                  items={visibleContents}
                  selectedIds={selectedIds}
                  onToggleItem={handleToggleItem}
                />
              ) : mode === "default" || mode === "add-content" ? (
              <section className="flex w-full flex-col items-center justify-center md:block">
                {contents.length === 0 && !loading ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <h3 className="mb-2 text-xl font-semibold text-foreground">
                      {t("room.noContentYet")}
                    </h3>
                    <p className="max-w-md text-muted-foreground">
                      {t("room.startBuildingSpace")}
                    </p>
                  </div>
                ) : (
                <div className="flex w-full flex-col items-center justify-center md:block">
                  <div className="w-full"></div>
                  <div className="flex w-full items-center justify-end md:mb-3 md:justify-between text-muted-foreground">
                    <div className="inline-flex h-10 items-center justify-center rounded-full bg-muted px-2 text-muted-foreground md:order-2 md:ml-auto md:px-1.5">
                      <button 
                        onClick={() => setViewMode('grid')}
                        className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                          viewMode === 'grid' 
                            ? 'bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-state={viewMode === 'grid' ? 'active' : 'inactive'}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <rect x="3" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="14" width="7" height="7" rx="1" />
                          <rect x="3" y="14" width="7" height="7" rx="1" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => setViewMode('list')}
                        className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                          viewMode === 'list' 
                            ? 'bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-state={viewMode === 'list' ? 'active' : 'inactive'}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M4 6h16" />
                          <path d="M4 12h16" />
                          <path d="M4 18h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="h-full w-full mt-4 md:mt-0">
                    <div className="grid gap-8 md:mt-6 md:gap-4">
                      <ContentGrid
                        items={visibleContents}
                        isLoading={loading}
                        buildHref={buildHref}
                        viewMode={viewMode}
                        rooms={allRooms}
                        onAddToRoom={handleAddToRoom}
                        onShare={handleContentShare}
                        onDelete={handleContentDeleteClick}
                      />
                    </div>
                  </div>
                </div>
                )}
              </section>
              ) : null}
            </div>
          </main>
        </div>

        {/* AI Tutor Panel slides in from right */}
        <RoomAITutorPanel
          isOpen={isTutorOpen}
          onClose={() => setIsTutorOpen(false)}
          roomId={roomId}
          teamId={room?.team_id}
          contents={contents}
        />
      </div>

      {/* Content Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("contentDelete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {`"${itemToDelete?.title || ""}" will be permanently deleted.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("contentDelete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t("contentDelete.deleting") : t("contentDelete.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Content Share Dialog */}
      {itemToShare && (
        <ContentShareDialog
          content={itemToShare}
          open={contentShareOpen}
          onOpenChange={setContentShareOpen}
          onVisibilityChange={handleContentVisibilityChange}
        />
      )}

      {/* Room Share Dialog */}
      {room && (
        <RoomShareDialog
          room={room}
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          onVisibilityChange={handleVisibilityChange}
        />
      )}
    </DashboardShell>
  );
};

export const RoomPage: React.FC = () => (
  <RoomModeProvider>
    <RoomPageInner />
  </RoomModeProvider>
);
